import {FC} from 'react';
import {usePersona} from 'src/permissions/hooks';
import {Download} from '@mui/icons-material';
import {Button, IconButton, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {formatFileName, isErrorObject} from 'src/utils/helper';
import {invoicesApi} from '../../../../api';
import {useBusinessId, useAppDispatch, useAppSelector} from '../../../../app/hooks';
import {AppDispatch} from '../../../../app/store';
import {preloaderChangeStatus} from '../../../../stores/common/commonSlice';
import {DownloadInvoicePdfTypes} from './DownloadInvoicePdf.types';
import theme from '../../../../theme';
import {NotifyError} from 'src/utils/notification';

type DownloadInvoicePdfType = ReturnType<typeof invoicesApi.useLazyGetInvoicePdfQuery>[0];
type DownloadRevenueInvoicePdfType = ReturnType<
    typeof invoicesApi.useLazyRevenueInvoicePdfQuery
>[0];

const handleDownloadFile =
    (
        trigger: DownloadInvoicePdfType,
        dispatch: AppDispatch,
        businessId: number,
        invoiceId: number,
        name: string,
        userId?: number
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                const params = {
                    invoiceId,
                    businessId,
                    userId,
                };
                const response = await trigger(params);
                const tempLink = document.createElement('a');
                tempLink.href = response?.data ?? '';
                tempLink.setAttribute('download', `${formatFileName(name)}`);
                tempLink.click();
                window.URL.revokeObjectURL(tempLink.href);
                return response;
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

const handleDownloadRevenueFile =
    (
        trigger: DownloadRevenueInvoicePdfType,
        dispatch: AppDispatch,
        businessId: number,
        invoiceId: number,
        name: string
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                const params = {
                    invoiceId,
                    businessId,
                };
                const response = await trigger(params);
                const tempLink = document.createElement('a');
                tempLink.href = response?.data ?? '';
                tempLink.setAttribute('download', `${formatFileName(name)}`);
                tempLink.click();
                window.URL.revokeObjectURL(tempLink.href);
                return response;
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const DownloadInvoicePdf: FC<DownloadInvoicePdfTypes> = ({
                                                                    invoiceId,
                                                                    name,
                                                                    businessId,
                                                                    isIcon,
                                                                    revenue,
                                                                    userId: propUserId,
                                                                }) => {
    const intl = useIntl();
    const persona = usePersona();
    const [getInvoicePdf] = invoicesApi.useLazyGetInvoicePdfQuery();
    const [getRevenueInvoicePdf] = invoicesApi.useLazyRevenueInvoicePdfQuery();
    const dispatch = useAppDispatch();
    const {authInfo} = useAppSelector((state) => state.common);
    // The one the caller named, or the business being worked in when it named none.
    const activeBusinessId = useBusinessId();
    const roleIsDriver = persona.isDriver;

    const userId = propUserId || (roleIsDriver ? authInfo?.id : undefined);

    // Which endpoint the file comes from is a property of the row, not of the reader: a revenue
    // invoice is issued to a business, an ordinary one to a person, and they are downloaded from
    // different places. Keyed on the viewer, the administrator's ordinary invoices asked the revenue
    // endpoint for a file it does not have.
    const handleClick = () => {
        revenue &&
        businessId &&
        handleDownloadRevenueFile(getRevenueInvoicePdf, dispatch, businessId, invoiceId, name)();
        !revenue &&
        handleDownloadFile(
            getInvoicePdf,
            dispatch,
            businessId ?? activeBusinessId,
            invoiceId,
            name,
            userId
        )();
    };

    return (
        <>
            {isIcon && (
                <IconButton
                    sx={{
                        width: '28px',
                        height: '28px',
                        p: '2px',
                        color: theme.palette.neutral[400],
                        backgroundColor: 'transparent',
                        '.MuiSvgIcon-root': {
                            fill: theme.palette.neutral[400],
                            width: '20px',
                        },
                    }}
                    onClick={handleClick}
                >
                    <Download/>
                </IconButton>
            )}
            {!isIcon && (
                <Button
                    fullWidth
                    onClick={handleClick}
                >
                    <Typography
                        sx={{
                            width: '100%',
                            textAlign: 'start',
                            fontSize: theme.spacing(14 / 8),
                            fontWeight: 400,
                            color: theme.palette.neutral[400],
                        }}
                    >
                        {intl.formatMessage({id: 'download'})}
                    </Typography>
                </Button>
            )}
        </>
    );
};
